/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import { useMemo } from 'react';
import { ZodType } from 'zod';
import { ColumnDef } from '@tanstack/react-table';
import type { TokenDefinition } from '@/components/ui/TokenizedInput/types';
import { deserialize } from '@/components/ui/TokenizedInput/serialization';
import {
  fieldsToTokenDefinitions,
  generateFilterFunction,
} from '@/lib/filters/field-to-filter';
import { fieldsToColumns } from '@/lib/filters/field-to-column';
import useDeepMemo from '@/hooks/use-deep-memo';

export interface UseFilterableTableOptions<T> {
  fields: Record<string, ZodType>;
  data: T[];
  filterValue: string;
  excludeColumns?: string[];
  excludeTokens?: string[];
  customColumns?: ColumnDef<T>[];
  customTokens?: TokenDefinition[];
  context?: any;
}

export interface UseFilterableTableResult<T> {
  tokenDefinitions: TokenDefinition[];
  filteredData: T[];
  columns: ColumnDef<T>[];
  tokens: any[]; // Token type from TokenizedInput
  isFiltering: boolean;
  resultCount: number;
  totalCount: number;
}

// TODO: Program defensively against errors ocurring in filters and
//       degrade gracefully by disabling the failing components and notifying the user

export function useFilterableTable<T extends Record<string, any>>({
  fields,
  data,
  filterValue,
  excludeColumns,
  excludeTokens,
  customColumns,
  customTokens,
  context,
}: UseFilterableTableOptions<T>): UseFilterableTableResult<T> {
  const memContext = useDeepMemo(() => context, [context]);

  // Generate token definitions from fields
  const tokenDefinitions = useMemo(() => {
    return fieldsToTokenDefinitions(fields, {
      exclude: excludeTokens,
      context: memContext,
      customTokens,
    });
  }, [fields, excludeTokens, customTokens, memContext]);

  // Generate filter function
  const filterFn = useMemo(() => {
    return generateFilterFunction<T>(fields, memContext);
  }, [fields, memContext]);

  // Parse filter value into tokens
  const { tokens, remainder } = useMemo(
    () => deserialize(filterValue),
    [filterValue],
  );

  // Filter data
  const filteredData = useMemo(() => {
    if (tokens.length === 0 && !remainder) return data;

    const result = data.filter(filterFn(tokens, remainder ?? ''));

    return result;
  }, [data, tokens, remainder, filterFn]);

  // Generate columns from fields
  const columns = useMemo(() => {
    return fieldsToColumns<T>(fields, {
      exclude: excludeColumns,
      customColumns,
      context: memContext,
    });
  }, [fields, excludeColumns, customColumns, memContext]);

  return {
    tokenDefinitions,
    filteredData,
    columns,
    tokens,
    isFiltering: tokens.length > 0 || !!remainder,
    resultCount: filteredData.length,
    totalCount: data.length,
  };
}
